// Observation chart modal — Phase 4 (2026-10-04).
//
// Shows the full observation history on an admission's encounter,
// grouped by vital code with a simple inline sparkline. No chart lib —
// an SVG polyline per code keeps the bundle small and the output
// predictable for the pilot clinic. If a vital has <2 points we render
// a value-list only.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ObservationChartPoint } from '@pamosi/shared';
import { ApiError, admissionsApi } from '../lib/api';
import { ModalShell } from './AdmitDialog';

interface Props {
    admissionId: string;
    onClose: () => void;
}

// Order + labels for the charted codes. Anything else observed shows in
// an "Other observations" section at the bottom.
const CHARTED_CODES: Array<{ code: string; label: string; unit: string }> = [
    { code: 'systolic_bp',       label: 'Systolic BP',   unit: 'mmHg' },
    { code: 'diastolic_bp',      label: 'Diastolic BP',  unit: 'mmHg' },
    { code: 'pulse_bpm',         label: 'Pulse',         unit: 'bpm'  },
    { code: 'temp_c',            label: 'Temperature',   unit: '°C'   },
    { code: 'spo2',              label: 'SpO₂',          unit: '%'    },
    { code: 'respiratory_rate',  label: 'Respiratory rate', unit: '/min' },
];

export function ObservationChartModal({ admissionId, onClose }: Props) {
    const q = useQuery({
        queryKey: ['admissions', admissionId, 'observations-chart'],
        queryFn: () => admissionsApi.observationsChart(admissionId),
    });

    const grouped = useMemo(() => {
        const map = new Map<string, ObservationChartPoint[]>();
        for (const p of q.data?.points ?? []) {
            const list = map.get(p.code) ?? [];
            list.push(p);
            map.set(p.code, list);
        }
        return map;
    }, [q.data]);

    return (
        <ModalShell title="Observation chart" onClose={onClose}>
            {q.isLoading && <div className="text-slate-500 text-sm">Loading chart…</div>}
            {q.isError && (
                <div className="pm-error text-sm">
                    {q.error instanceof ApiError ? q.error.message : 'Could not load chart.'}
                </div>
            )}
            {q.data && q.data.points.length === 0 && (
                <div className="text-slate-500 text-sm italic">
                    No observations recorded yet for this admission.
                </div>
            )}
            {q.data && q.data.points.length > 0 && (
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    {CHARTED_CODES.map((cfg) => {
                        const pts = grouped.get(cfg.code) ?? [];
                        if (pts.length === 0) return null;
                        return (
                            <ChartRow key={cfg.code} label={cfg.label} unit={cfg.unit} points={pts} />
                        );
                    })}
                    <OtherObservations
                        points={(q.data.points ?? []).filter(
                            (p) => !CHARTED_CODES.some((c) => c.code === p.code),
                        )}
                    />
                </div>
            )}
        </ModalShell>
    );
}

function ChartRow({
    label,
    unit,
    points,
}: {
    label: string;
    unit: string;
    points: ObservationChartPoint[];
}) {
    const values = points
        .map((p) => p.value_numeric)
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    const min = values.length ? Math.min(...values) : null;
    const max = values.length ? Math.max(...values) : null;
    const latest = points[points.length - 1];
    const earliest = points[0];
    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-baseline justify-between">
                <div className="text-slate-800 text-sm font-semibold">{label}</div>
                <div className="text-slate-500 text-xs">{unit}</div>
            </div>
            {values.length >= 2 && (
                <Sparkline values={values} />
            )}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
                {latest?.value_numeric != null && (
                    <span>
                        <span className="text-slate-500">Latest:</span>{' '}
                        <span className="font-medium">{latest.value_numeric}</span>{' '}
                        <span className="text-slate-500">
                            @ {new Date(latest.observed_at).toLocaleString()}
                        </span>
                    </span>
                )}
                {min != null && max != null && (
                    <span>
                        <span className="text-slate-500">Range:</span>{' '}
                        <span className="font-medium">{min}–{max}</span>
                    </span>
                )}
                {earliest && earliest !== latest && (
                    <span>
                        <span className="text-slate-500">Points:</span> {points.length}
                    </span>
                )}
            </div>
        </div>
    );
}

function Sparkline({ values }: { values: number[] }) {
    const W = 320;
    const H = 60;
    const PAD = 4;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const step = values.length > 1 ? (W - PAD * 2) / (values.length - 1) : 0;
    const points = values
        .map((v, i) => {
            const x = PAD + i * step;
            const y = PAD + (H - PAD * 2) * (1 - (v - min) / span);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="mt-2 w-full h-14"
            preserveAspectRatio="none"
            role="img"
            aria-label="Trend sparkline"
        >
            <polyline
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="text-sky-600"
                points={points}
            />
        </svg>
    );
}

function OtherObservations({ points }: { points: ObservationChartPoint[] }) {
    if (points.length === 0) return null;
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="text-slate-800 text-sm font-semibold mb-2">
                Other observations
            </div>
            <ul className="space-y-1 text-xs text-slate-700">
                {points.map((p) => (
                    <li key={p.id}>
                        <span className="font-medium">{p.code}:</span>{' '}
                        {p.value_numeric ?? p.value_text ?? '—'}
                        {p.unit ? ` ${p.unit}` : ''}{' '}
                        <span className="text-slate-500">
                            · {new Date(p.observed_at).toLocaleString()}
                            {p.observed_by_full_name ? ` · ${p.observed_by_full_name}` : ''}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
